// AnimationController.js - Simplified animation orchestration (100 lines!)
import { getAnimationContainer } from '@containers/MapContainer/AnimationLayer';
import { calculateMSBasedOnTicks } from '@utils/animationTiming';

class AnimationController {
  constructor(rqItem, onComplete) {
    this.animations = rqItem?.animations;
    this.onComplete = onComplete;
    this.cancelled = false;
    this.startTimeouts = [];  // For delayed starts
    this.destroyTimeouts = new Map();  // For each animation's destruction
    this.activeAnimations = new Map();
  }

  start() {
    if (this.cancelled) return;

    // Log all animations we're about to process
    console.log('[AnimationController] Starting with animations:', this.animations.map(a => a.animation_name));

    if( this.animations.length === 0 ) {
      console.warn('[AnimationController] No animations defined, finishing immediately');
      this.finish();
      return;
    }

    const container = getAnimationContainer();
    if (!container ) {
      // No visuals possible, just wait and complete
      console.warn('[AnimationController] No container to run');
      this.finish();
      return;
    }

    // Validate and schedule animations
    const validAnimations = this.animations.filter(anim => 
      anim.animation_name && 
      anim.duration !== undefined && 
      anim.delay !== undefined
    );

    if (validAnimations.length === 0) {
      console.warn('[AnimationController] No valid animations to run');
      this.waitAndFinish();
      return;
    }

    // Schedule each animation at its delay
    validAnimations.forEach(anim => {
      const delayMS = calculateMSBasedOnTicks(anim.delay);
      //console.log(`[AnimationController] Scheduling ${anim.animation_name} to start in ${anim.delay} ticks (${delayMS}ms)`);

      const timeout = setTimeout(() => {
        if (!this.cancelled) {
          //console.log(`[AnimationController] Starting ${anim.animation_name} animation with timeout of ${anim.duration} ticks`);
          this.runAnimation(anim, container);
        }
      }, delayMS);

      this.startTimeouts.push(timeout);
    });

    // Calculate when all animations should be done
    const animationTimings = validAnimations.map(a => ({
      name: a.animation_name,
      delay: a.delay,
      duration: a.duration,
      endTime: (a.delay) + (a.duration)
    }));

    //console.log('[AnimationController] Animation timings:', animationTimings);

    const maxEndTime = Math.max(...animationTimings.map(a => a.endTime));

    //console.log('[AnimationController] Max end time:', maxEndTime, 'ticks =', calculateMSBasedOnTicks(maxEndTime), 'ms');

    // Set a timeout for completion
    const completeTimeout = setTimeout(() => {
      if (!this.cancelled) {
        //console.info('[AnimationController] Complete timeout triggered, finishing...');
        this.finish();
      }
    }, calculateMSBasedOnTicks(maxEndTime));

    this.startTimeouts.push(completeTimeout);
  }

  async runAnimation(animData, container) {
    const AnimationClass = await this.loadAnimationClass(animData);
    if (!AnimationClass){
      console.error(`[AnimationController] Failed to load animation class for ${animData.animation_name}`);
      return;
    }

    const id = `${animData.animation_name}_${Date.now()}`;

    // Pass flat data structure
    const instance = new AnimationClass({
      ...animData,  // Spread all animation properties flat
      container: container,
      durationMS: calculateMSBasedOnTicks(animData.duration),
      delayMS: calculateMSBasedOnTicks(animData.delay)
    });

    this.activeAnimations.set(id, instance);

    // VALIDATE start method exists
    if (!instance.start) {
      throw new Error(`[AnimationController] Animation class ${AnimationClass.name} missing start() method!`);
    }
    instance.start();

    // Controller manages destruction
    const destroyTimeout = setTimeout(() => {
      if (!this.cancelled && this.activeAnimations.has(id)) {
        console.log(`[AnimationController] Destroying ${animData.animation_name} after ${animData.duration} ticks`);

        // VALIDATE destroy method exists
        if (!instance.destroy) {
          throw new Error(`[AnimationController] Animation class missing destroy() method!`);
        }
        instance.destroy();

        this.activeAnimations.delete(id);
        this.destroyTimeouts.delete(id);
      }
    }, calculateMSBasedOnTicks(animData.duration));

    this.destroyTimeouts.set(id, destroyTimeout);
  }

  // Convert snake_case to PascalCase
  toPascalCase(str) {
    return str.split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join('');
  }

  async loadAnimationClass(animData) {
    const { animation_name, category } = animData;


    // 1. Try specific animation
    try {
      const pascalName = this.toPascalCase(animation_name);
      const module = await import(`./animations/${pascalName}Animation.js`);
      return module.default;
    } catch{
        //console.error('[DEBUG] error loading animation:', error);
      // Specific animation not found, continue to fallback
    }

    // 2. Try category handler
    if (category) {
      try {
        const categoryName = category.charAt(0).toUpperCase() + category.slice(1);
        const module = await import(`./animations/Category${categoryName}Animation.js`);
        //console.info(`[AnimationController] Using category ${category} for ${animation_name}`);
        return module.default;
      } catch {
        // Category handler not found, continue to default
      }
    }

    // 3. Use default
    try {
      const module = await import('./DefaultAnimation.js');
      console.warn(`[AnimationController] Using default for ${animation_name}`);
      return module.default;
    } catch {
      console.error(`[AnimationController] No animation available for ${animation_name}`);
      return null;
    }
  }

  waitAndFinish() {
    const timeout = setTimeout(() => {
      if (!this.cancelled) this.finish();
    }, calculateMSBasedOnTicks(5)); // Default 5 tick wait
    this.startTimeouts.push(timeout);
  }

  finish() {
    if (this.cancelled) return;
    if (this.onComplete) {
      this.onComplete();
    }
  }

  cancel() {
    this.cancelled = true;

    // Clear all start timeouts
    this.startTimeouts.forEach(t => clearTimeout(t));
    this.startTimeouts = [];

    // Clear all destruction timeouts
    this.destroyTimeouts.forEach(t => clearTimeout(t));
    this.destroyTimeouts.clear();

    // Immediately destroy all active animations
    this.activeAnimations.forEach((instance) => {
      if (!instance.destroy) {
        throw new Error('[AnimationController] Animation instance missing destroy() method!');
      }
      instance.destroy();
    });
    this.activeAnimations.clear();
  }
}

export default AnimationController;